import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { ErrorState } from '../ui/error-state'
import { Input } from '../ui/input'
import { LoadingTable } from '../ui/loading-states'
import { Pagination } from '../ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { usePharmaExpiryRisk } from '../../hooks/usePharmaAnalytics'
import {
  formatCurrency,
  formatIsoDate,
  formatNumber,
} from '../../utils/dateFormat'
import type {
  ExpiryBucket,
  PharmaExpiryRiskFilters,
} from '../../types/pharma-analytics.types'

interface ExpiryRiskTabProps {
  filters: PharmaExpiryRiskFilters
  onFiltersChange: (
    update: (prev: PharmaExpiryRiskFilters) => PharmaExpiryRiskFilters
  ) => void
}

/** Цвет плитки бакета: чем ближе срок, тем тревожнее. */
const BUCKET_TONE: Record<ExpiryBucket, string> = {
  expired: 'border-destructive/40 bg-destructive/10 text-destructive',
  d30: 'border-destructive/30 bg-destructive/5 text-destructive',
  d90: 'border-amber-400/40 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-500',
  d180: 'border-amber-300/40 bg-amber-50/60 text-amber-700 dark:bg-amber-950/20 dark:text-amber-500',
  later: 'border-border bg-muted/40 text-muted-foreground',
  perpetual: 'border-border bg-muted/40 text-muted-foreground',
  unknown: 'border-border bg-muted/40 text-muted-foreground',
}

export function ExpiryRiskTab({ filters, onFiltersChange }: ExpiryRiskTabProps) {
  const { data, isLoading, error, refetch } = usePharmaExpiryRisk(filters)

  const rows = data?.rows ?? []
  const limit = filters.limit ?? 50
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / limit))

  const setBucket = (bucket?: ExpiryBucket) =>
    onFiltersChange((prev) => ({ ...prev, bucket, page: 1 }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Риск списания по сроку годности</CardTitle>
        <CardDescription>
          Срез остатков на {formatIsoDate(data?.asOf)}. Стоимость считается только по
          позициям с известной ценой — там, где цены нет, стоит прочерк, а не ноль.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Плитки бакетов остаются полными при любом фильтре — иначе переключение
            вкладки выглядело бы как «проблема исчезла». */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {(data?.buckets ?? []).map((bucket) => {
            const active = filters.bucket === bucket.bucket
            return (
              <button
                key={bucket.bucket}
                type="button"
                onClick={() => setBucket(active ? undefined : bucket.bucket)}
                className={`rounded-lg border px-3 py-2 text-left transition ${
                  BUCKET_TONE[bucket.bucket]
                } ${active ? 'ring-2 ring-tco-blue-500 ring-offset-1' : ''}`}
              >
                <div className="text-lg font-bold tabular-nums">
                  {formatNumber(bucket.positions)}
                </div>
                <div className="text-[11px] leading-tight">{bucket.label}</div>
                <div className="text-[11px] font-medium tabular-nums mt-0.5">
                  {formatCurrency(bucket.value, { compact: true })}
                </div>
              </button>
            )
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Поиск по наименованию"
            className="max-w-xs"
            defaultValue={filters.search ?? ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onFiltersChange((prev) => ({
                  ...prev,
                  search: (e.target as HTMLInputElement).value || undefined,
                  page: 1,
                }))
              }
            }}
          />
          {filters.bucket && (
            <Button variant="outline" size="sm" onClick={() => setBucket(undefined)}>
              Сбросить фильтр бакета
            </Button>
          )}
          <div className="ml-auto text-sm text-muted-foreground tabular-nums">
            Найдено: {formatNumber(data?.total ?? 0)}
          </div>
        </div>

        {isLoading ? (
          <LoadingTable rows={8} columns={7} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить риск списания"
            description={(error as Error)?.message}
            onAction={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Позиций не найдено
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Препарат</TableHead>
                    <TableHead>Склад</TableHead>
                    <TableHead>Партия</TableHead>
                    <TableHead>Срок годности</TableHead>
                    <TableHead className="text-right">Остаток</TableHead>
                    <TableHead className="text-right">Цена</TableHead>
                    <TableHead className="text-right">Стоимость</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={`${row.warehouseCode}-${row.medicationCode}-${row.batchNumber}`}
                    >
                      <TableCell className="font-medium max-w-xs">
                        <div className="truncate" title={row.medicationName}>
                          {row.medicationName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.medicationCode}
                          {row.account && ` · счёт ${row.account}`}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <div className="truncate" title={row.warehouseName ?? ''}>
                          {row.warehouseName ?? '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.batchNumber || '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.bucket === 'perpetual' ? (
                          <Badge variant="secondary">срок не указан</Badge>
                        ) : row.bucket === 'unknown' ? (
                          <Badge variant="secondary">не распознан</Badge>
                        ) : (
                          <>
                            {formatIsoDate(row.expiryDate)}
                            <div
                              className={`text-xs ${
                                (row.daysLeft ?? 0) < 0
                                  ? 'text-destructive'
                                  : (row.daysLeft ?? 0) <= 90
                                    ? 'text-amber-600'
                                    : 'text-muted-foreground'
                              }`}
                            >
                              {row.daysLeft === null
                                ? ''
                                : row.daysLeft < 0
                                  ? `просрочено на ${-row.daysLeft} дн.`
                                  : `осталось ${row.daysLeft} дн.`}
                            </div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {formatNumber(row.quantity, { maximumFractionDigits: 3 })}
                        {row.unitHint && (
                          <span
                            className="text-xs text-muted-foreground ml-1"
                            title="Единица взята из движений — в остатках 1С её нет"
                          >
                            {row.unitHint}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.pricePurchase)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {formatCurrency(row.value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={filters.page ?? 1}
              totalPages={totalPages}
              onPageChange={(page) =>
                onFiltersChange((prev) => ({ ...prev, page }))
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
